<template>
  <BottomSheet v-model="open" elevated :title="t('payout.fundTitle')">
    <div class="fv-desc">{{ t(`payout.fundDesc.${reason}`) }}</div>
    <div class="wa2-in">
      <input
        v-model="pwd"
        type="password"
        inputmode="numeric"
        maxlength="6"
        autocomplete="off"
        :placeholder="t('payout.fundPh')"
        :disabled="submitting"
        @keyup.enter="onConfirm"
      />
    </div>
    <div v-if="err" class="wx-err">{{ err }}</div>
    <button
      type="button"
      class="btn btn-primary btn-block mt16"
      :disabled="submitting"
      @click="onConfirm"
    >
      {{ t('payout.fundConfirm') }}
    </button>
    <div class="wx-hint">{{ t('payout.fundHint') }}</div>
  </BottomSheet>
</template>

<script setup lang="ts">
import { verifyFundPassword } from '~/api/security'
import { ApiError, FUND_PASSWORD_LOCKED_CODE } from '~/types/api'
import { fundLockedMessage } from '~/utils/retryWait'

const { t } = useI18n()
const { error: toastError } = useNotify()
const { open, reason, closeFundGate, resolveFundGate } = useFundGate()

const pwd = ref('')
const err = ref('')
const submitting = ref(false)

watch(open, v => {
  pwd.value = ''
  err.value = ''
  submitting.value = false
  if (!v) closeFundGate()
})

const onConfirm = async () => {
  if (submitting.value) return
  const v = pwd.value.trim()
  if (!/^\d{6}$/.test(v)) {
    err.value = t('payout.errFundFormat')
    return
  }
  err.value = ''
  submitting.value = true
  try {
    const data = await verifyFundPassword({ fund_password: v })
    await resolveFundGate({
      fundPassword: v,
      verificationId: data?.verification_id,
    })
  } catch (e) {
    pwd.value = ''
    // 1030 锁定：按服务端给的剩余时间提示（不再写死「2 小时」）
    if (e instanceof ApiError && e.code === FUND_PASSWORD_LOCKED_CODE) {
      closeFundGate()
      toastError(fundLockedMessage(t, e.retryAfterSeconds))
      return
    }
    err.value = e instanceof ApiError && e.message ? e.message : t('payout.errFundFail')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
@use '~/pages/me/security/styles/sec-form' as *;

.fv-desc {
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.6;
  margin: 2px 0 12px;
}
</style>
